import { ReactElement, useMemo } from 'react';
import { Card, Typography, Stack } from '@mui/material';
import { max } from 'lodash';
import moment from 'moment-timezone';

import { Link } from '~components/index';
import { PATHS } from '~constants/index';
import { RouteItemStateType, ItemStateTypes } from '~globals/types/enums';
import { getStringifyUrlQueryString } from '~utils/router';
import { formatterDate } from '~utils/formatter';

import { DeliveryStatesProps, DeliveryStateListItem } from './types';
import {
  DeliveryStatesCardContent,
  DeliveryStatesLinearProgress,
} from './styles';

const normalizedValue = (
  value: number,
  minVal: number,
  maxVal: number,
): number => ((value - minVal) * 100) / (maxVal - minVal);

const currentDate = moment();

const commonsDates = {
  dateFrom: formatterDate(currentDate, { format: 'MM-DD-YYYY' }),
  dateTo: formatterDate(currentDate, { format: 'MM-DD-YYYY' }),
};

const DeliveryStates = ({
  pending,
  onAgenda,
  inProgress,
  unComplete,
  complete,
  withoutVisiting,
}: DeliveryStatesProps): ReactElement => {
  const maxValue = useMemo(
    () =>
      max([
        pending,
        onAgenda,
        inProgress,
        unComplete,
        complete,
        withoutVisiting,
      ]) || 100,
    [pending, onAgenda, inProgress, unComplete, complete, withoutVisiting],
  );

  const listState = useMemo<DeliveryStateListItem[]>(
    () => [
      {
        label: 'Recepcionado',
        color: '#BF18B6',
        value: pending,
        itemStateTypeId: ItemStateTypes.New,
        currentTab: 'pending',
      },
      {
        label: 'A Despachar',
        color: '#1976d2',
        value: onAgenda,
        routeItemStateId: RouteItemStateType.OnAgenda,
        currentTab: 'on-agenda',
        ...commonsDates,
      },
      {
        label: 'En viaje',
        color: '#5618b6',
        value: inProgress,
        routeItemStateId: RouteItemStateType.InProgress,
        currentTab: 'on-agenda',
        ...commonsDates,
      },
      {
        label: 'Incompleta',
        color: '#ff0000',
        value: unComplete,
        currentTab: 'finalized',
        routeItemStateId: RouteItemStateType.FinalizedError,
        ...commonsDates,
      },
      {
        label: 'Cierre forzoso',
        color: '#7f5f01',
        value: withoutVisiting,
        currentTab: 'finalized',
        routeItemStateId: RouteItemStateType.WithoutVisiting,
        ...commonsDates,
      },
      {
        label: 'Completa',
        color: '#4caf50',
        value: complete,
        currentTab: 'finalized',
        routeItemStateId: RouteItemStateType.FinalizedSuccess,
        ...commonsDates,
      },
    ],
    [pending, onAgenda, inProgress, unComplete, complete, withoutVisiting],
  );

  return (
    <Card>
      <DeliveryStatesCardContent>
        <Stack
          spacing={2}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h6" fontWeight="bold">
            Estado de pedidos
          </Typography>

          <Link
            to={PATHS.ORDERS.BASE}
            color="primary"
            variant="caption"
            underline="hover"
          >
            Ver pedidos
          </Link>
        </Stack>

        <Stack spacing={2}>
          {listState.map((stateData) => (
            <Stack spacing={1} key={`progress-${stateData.label}`}>
              <Link
                to={getStringifyUrlQueryString({
                  url: PATHS.ORDERS.BASE,
                  query: {
                    currentTab: stateData.currentTab,
                    state: stateData.routeItemStateId,
                    itemState: stateData.itemStateTypeId,
                    dateFrom: stateData.dateFrom,
                    dateTo: stateData.dateTo,
                  },
                })}
                color="text.primary"
                variant="caption"
                underline="hover"
              >
                {stateData.label}
              </Link>

              <Stack direction="row" spacing={1}>
                <DeliveryStatesLinearProgress
                  variant="determinate"
                  value={normalizedValue(stateData.value, 0, maxValue)}
                  customColor={stateData.color}
                />

                <Typography variant="caption">{stateData.value}</Typography>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </DeliveryStatesCardContent>
    </Card>
  );
};

export default DeliveryStates;
